import type { KeyboardEvent, ReactNode } from 'react';

/**
 * V7-P4A shared data-table primitive (design-language 03 P10; registry
 * component.generic-table -- built ONCE, no more inline <table> markup on
 * retrofitted surfaces). Wraps the EXISTING `.table` idiom (hairline
 * dividers, micro-label header, hover wash) in the overflow-x scroll
 * container so a wide table never breaks the page horizontally (mobile
 * posture: the table scrolls inside its own container).
 *
 * PRESENTATION-ONLY: rows arrive fully resolved from the caller; this
 * component fetches nothing, mutates nothing, derives nothing, and embeds
 * no business logic. Empty rows render the caller's OWN honest empty node
 * (03 P11 -- never a generic "no data", never a blank hole).
 *
 * CROSS-ROLE-HOME-GRAMMAR-V1 (founder ruling D9) EXTENDS this primitive with
 * GROUPING rather than forking a second table: ONE table declares the columns
 * ONCE and carries a `<tbody>` per group, so the columns stay aligned across
 * every group and down the whole screen. The doctor worklist previously
 * stacked per-group mini-tables that each re-declared their own widths, so
 * nothing lined up and the two role homes could not read as the same object.
 * The grouped mode is ADDITIVE -- an adopter that passes no `groups` renders
 * byte-identically to before.
 */

export interface DataTableColumn<Row> {
  readonly key: string;
  readonly header: ReactNode;
  readonly render: (row: Row) => ReactNode;
  /** 'end' right-aligns numeric/money cells (tabular scan). */
  readonly align?: 'start' | 'end';
  /**
   * COLUMN-WIDTHS-V1 (founder authorisation 2026-09-09, condition A). A CSS
   * width for this column, applied through a `<colgroup>`.
   *
   * WHY IT EXISTS: `[data-visual='v1'] .ac-table` is `table-layout: fixed`
   * (dashboard.css), so with no widths every column takes an identical share
   * and a table starves its widest column while over-serving its narrowest.
   * Screens worked around it with their own `th:nth-child(n)` rules in a
   * screen-scoped stylesheet -- a width map that could not be reused. This
   * puts the allocation on the column definition, where the caller already
   * declares everything else about that column.
   *
   * IDENTICAL AT REST: the `<colgroup>` is rendered ONLY when at least one
   * column declares a width. A table whose columns declare none emits
   * byte-identical markup to before this prop existed.
   */
  readonly width?: string;
}

/**
 * One group inside THE ONE table (D9). Presentation only: the caller has
 * already decided membership and order -- this primitive neither sorts,
 * ranks, nor prioritises anything (picking who comes first is a tenant
 * policy nobody has ruled; founder, 2026-08-31).
 */
export interface DataTableGroup<Row> {
  readonly key: string;
  /** The group's own name, e.g. "Present" / "Expected". */
  readonly label: ReactNode;
  /** A count or other quiet trailing fact, printed beside the name. */
  readonly meta?: ReactNode;
  readonly rows: readonly Row[];
  readonly testId?: string;
}

export interface DataTableProps<Row> {
  readonly columns: ReadonlyArray<DataTableColumn<Row>>;
  readonly rows: readonly Row[];
  readonly rowKey: (row: Row) => string | number;
  /** Per-row modifier classes (e.g. the severity-strip row idiom). */
  readonly rowClassName?: (row: Row) => string | undefined;
  /** The surface's OWN honest empty state, rendered INSTEAD of the table. */
  readonly empty?: ReactNode;
  /** Accessible table label (semantic label law). */
  readonly label?: string;
  readonly testId?: string;
  /** Opt in to a keyboard-focusable, named horizontal scroll region. */
  readonly scrollable?: boolean;
  readonly scrollLabel?: string;
  /**
   * OWNER-OPERATING-SYSTEM-V1 additive extensions (the enterprise table
   * contract, growth brief s.20.1 -- this primitive EXTENDS, never forks):
   * stickyHeader pins the header row inside the scroll container;
   * countLine prints the disclosed range/total line under the table
   * ("1-5 of 128"; the cut is always disclosed); onRowClick makes the
   * whole row the drill affordance (focusable, Enter activates).
   */
  readonly stickyHeader?: boolean;
  readonly countLine?: ReactNode;
  readonly onRowClick?: (row: Row) => void;
  /**
   * CROSS-ROLE-HOME-GRAMMAR-V1 (founder ruling D9): ONE TABLE, GROUPED
   * WITHIN IT. Given groups, the primitive renders ONE table whose header
   * row is declared ONCE and one `<tbody>` per group, each opened by a
   * full-width group row. Columns therefore stay aligned down the WHOLE
   * screen instead of every group re-declaring its own widths -- the
   * defect D9 names, and the reason the doctor worklist and the front-desk
   * board could not read as the same object. Reference pattern: Linear's
   * grouped list. ADDITIVE: omit it and every existing adopter renders
   * byte-identically from `rows`.
   */
  readonly groups?: ReadonlyArray<DataTableGroup<Row>>;
  /**
   * Per-row test id, so a surface adopting this primitive keeps the row
   * handle its walks and captures already name. Additive: omit it and the
   * row markup is byte-identical to before.
   */
  readonly rowTestId?: (row: Row) => string | undefined;
  /**
   * ADORACARE-VISUAL-SYSTEM-V1 (the SELECTED grammar): TRUE for the row the
   * hosting surface currently has selected -- rendered as `aria-selected`
   * on the <tr>, so the selection carries meaning, not only a wash. Omitted
   * => no attribute (every existing adopter renders byte-identically).
   */
  readonly rowSelected?: (row: Row) => boolean;
}

export default function DataTable<Row>({
  columns,
  rows,
  rowKey,
  rowClassName,
  empty,
  label,
  testId,
  stickyHeader,
  countLine,
  onRowClick,
  groups,
  rowTestId,
  rowSelected,
  scrollable = false,
  scrollLabel,
}: DataTableProps<Row>): JSX.Element | null {
  // D9: grouped mode counts the rows the GROUPS carry, so an empty grouped
  // table still renders the caller's own honest empty node rather than an
  // eyebrow row above nothing.
  const grouped = groups !== undefined;
  const total = grouped
    ? groups.reduce((n, g) => n + g.rows.length, 0)
    : rows.length;
  if (total === 0) {
    return empty === undefined ? null : <>{empty}</>;
  }
  const rowProps = (row: Row) => {
    if (!onRowClick) return {};
    return {
      onClick: () => onRowClick(row),
      onKeyDown: (e: KeyboardEvent) => {
        if (e.key === 'Enter') onRowClick(row);
      },
      tabIndex: 0,
    };
  };
  /** ONE row renderer for both modes -- a grouped row and a flat row are the
   *  same row (D9: the group changes where a row sits, never what it is). */
  const bodyRow = (row: Row): JSX.Element => {
    const cls = rowClassName?.(row);
    const linkCls = onRowClick
      ? cls
        ? `${cls} ac-table__row--link`
        : 'ac-table__row--link'
      : cls;
    const tid = rowTestId?.(row);
    return (
      <tr
        key={rowKey(row)}
        className={linkCls}
        {...(tid !== undefined ? { 'data-testid': tid } : {})}
        {...(rowSelected?.(row) === true ? { 'aria-selected': true } : {})}
        {...rowProps(row)}
      >
        {columns.map((col) => (
          <td
            key={col.key}
            className={col.align === 'end' ? 'ac-table__cell--end' : undefined}
          >
            {col.render(row)}
          </td>
        ))}
      </tr>
    );
  };
  // COLUMN-WIDTHS-V1: absent unless a caller declares a width, so existing
  // adopters render byte-identically.
  const hasWidths = columns.some((c) => c.width !== undefined);
  return (
    <div className="ac-table-scroll" data-testid={testId}
      {...(scrollable ? { tabIndex: 0, role: 'region', 'aria-label': scrollLabel || label || 'Table' } : {})}>
      <table
        className={
          stickyHeader ? 'table ac-table ac-table--sticky' : 'table ac-table'
        }
        aria-label={label}
      >
        {hasWidths ? (
          <colgroup>
            {columns.map((col) => (
              <col
                key={col.key}
                {...(col.width !== undefined ? { style: { width: col.width } } : {})}
              />
            ))}
          </colgroup>
        ) : null}
        <thead>
          <tr>
            {columns.map((col) =>
              col.align === 'end' ? (
                <th key={col.key} className="ac-table__cell--end">
                  {col.header}
                </th>
              ) : (
                <th key={col.key}>{col.header}</th>
              ),
            )}
          </tr>
        </thead>
        {grouped ? (
          groups.map((group) =>
            group.rows.length === 0 ? null : (
              <tbody
                key={group.key}
                className="ac-table__group"
                {...(group.testId !== undefined
                  ? { 'data-testid': group.testId }
                  : {})}
              >
                <tr className="ac-table__grouprow">
                  {/* ONE full-width group row inside the SAME table: the
                      columns below it are the table's, never the group's. */}
                  <th scope="colgroup" colSpan={columns.length}>
                    {group.label}
                    {group.meta !== undefined && group.meta !== null ? (
                      <span className="ac-table__groupmeta">{group.meta}</span>
                    ) : null}
                  </th>
                </tr>
                {group.rows.map((row) => bodyRow(row))}
              </tbody>
            ),
          )
        ) : (
          <tbody>{rows.map((row) => bodyRow(row))}</tbody>
        )}
      </table>
      {countLine !== undefined && countLine !== null ? (
        <p className="ac-table__count">{countLine}</p>
      ) : null}
    </div>
  );
}
